// UnifiedEventBindingSystem.js
// Enterprise-grade event binding system to eliminate duplicate handlers
// Fixes double-firing events that cause "Transition already in progress" errors
// Integrates with mobile/desktop/shared architecture

import { navigationHandler } from './NavigationActionHandler.js';
import { MobileDetectionService } from './MobileDetectionService.js';

/**
 * Unified event binding system for template scenes
 * Eliminates duplicate event handlers that cause navigation conflicts
 * Implements single event binding pattern across all scenes
 */
export class UnifiedEventBindingSystem {
    static _instance = null;
    
    /**
     * Singleton pattern for consistent event management
     */
    static getInstance() {
        if (!UnifiedEventBindingSystem._instance) {
            UnifiedEventBindingSystem._instance = new UnifiedEventBindingSystem();
        }
        return UnifiedEventBindingSystem._instance;
    }
    
    constructor() {
        if (UnifiedEventBindingSystem._instance) {
            return UnifiedEventBindingSystem._instance;
        }
        
        this.boundScenes = new Set();
        this.eventHandlers = new Map();
        UnifiedEventBindingSystem._instance = this;
    }
    
    /**
     * Bind all necessary events for a template scene
     * Replaces multiple binding methods with single, comprehensive binding
     * Respects mobile/desktop/shared architecture
     * @param {BaseMenuScene} scene - Scene to bind events for
     * @param {string} overlayId - DOM overlay ID (default: 'auth-menu-dom-overlay')
     */
    bindSceneEvents(scene, overlayId = 'auth-menu-dom-overlay') {
        const sceneKey = scene.scene.key;
        
        // Prevent duplicate binding for same scene
        if (this.boundScenes.has(sceneKey)) {
            console.warn(`⚠️ Events already bound for scene: ${sceneKey}`);
            return;
        }
        
        // Respect mobile/desktop architecture
        const isMobile = MobileDetectionService.isMobile();
        console.log(`🔗 UnifiedEventBindingSystem: Binding events for ${sceneKey} (${isMobile ? 'mobile' : 'desktop'})`);
        
        // Set scene context for navigation handler
        navigationHandler.setCurrentScene(scene);
        
        // Wait for DOM to be ready
        setTimeout(() => {
            this._bindNavigationEvents(scene, overlayId);
            this._bindGameEvents(scene, overlayId);
            this._bindHeaderEvents(scene, overlayId);
            this._bindSpecialEvents(scene, overlayId);
            
            this.boundScenes.add(sceneKey);
            console.log(`✅ Event binding complete for ${sceneKey}`);
        }, 50);
    }
    
    /**
     * Unbind events for a scene (cleanup)
     * @param {BaseMenuScene} scene - Scene to unbind events for
     * @param {string} overlayId - DOM overlay ID
     */
    unbindSceneEvents(scene, overlayId = 'auth-menu-dom-overlay') {
        const sceneKey = scene.scene.key;
        
        if (!this.boundScenes.has(sceneKey)) {
            return; // No events to unbind
        }
        
        console.log(`🔓 UnifiedEventBindingSystem: Unbinding events for ${sceneKey}`);
        
        const overlay = document.getElementById(overlayId);
        if (!overlay) return;
        
        // Remove all event listeners for this scene
        const handlers = this.eventHandlers.get(sceneKey);
        if (handlers) {
            handlers.forEach(({ element, event, handler }) => {
                element.removeEventListener(event, handler);
            });
            this.eventHandlers.delete(sceneKey);
        }
        
        this.boundScenes.delete(sceneKey);
        console.log(`✅ Event unbinding complete for ${sceneKey}`);
    }
    
    /**
     * Bind navigation events (nav-item[data-action])
     * @private
     */
    _bindNavigationEvents(scene, overlayId) {
        const overlay = document.getElementById(overlayId);
        if (!overlay) {
            console.error(`🚨 Overlay not found: ${overlayId}`);
            return;
        }
        
        const navItems = overlay.querySelectorAll('.nav-item[data-action]');
        console.log(`🧭 Found ${navItems.length} navigation items`);
        
        navItems.forEach(item => {
            const action = item.dataset.action;
            if (!action) return;
            
            const handler = (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Visual feedback
                this._addClickFeedback(item);
                
                // Handle navigation via centralized system
                navigationHandler.handleTemplateAction(action, scene);
            };
            
            item.addEventListener('click', handler, { passive: false });
            this._trackEventHandler(scene.scene.key, item, 'click', handler);
        });
    }
    
    /**
     * Bind game events (nav-item[data-game])
     * @private
     */
    _bindGameEvents(scene, overlayId) {
        const overlay = document.getElementById(overlayId);
        if (!overlay) return;
        
        const gameItems = overlay.querySelectorAll('.nav-item[data-game]');
        console.log(`🎮 Found ${gameItems.length} game items`);
        
        gameItems.forEach(item => {
            const game = item.dataset.game;
            if (!game) return;
            
            const handler = (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                console.log(`🎮 CLICK DETECTED: Game "${game}" clicked from ${scene.scene?.key || 'unknown scene'}`);
                
                // Visual feedback
                this._addClickFeedback(item);
                
                // Handle game action via centralized system
                console.log(`🎮 Calling navigationHandler.handleGameAction("${game}", scene)`);
                navigationHandler.handleGameAction(game, scene);
            };
            
            item.addEventListener('click', handler, { passive: false });
            this._trackEventHandler(scene.scene.key, item, 'click', handler);
        });
    }
    
    /**
     * Bind header button events (header-btn[data-action])
     * @private
     */
    _bindHeaderEvents(scene, overlayId) {
        const overlay = document.getElementById(overlayId);
        if (!overlay) return;
        
        const headerButtons = overlay.querySelectorAll('.header-btn[data-action], button[data-action]');
        console.log(`📋 Found ${headerButtons.length} header buttons`);
        
        headerButtons.forEach(button => {
            const action = button.dataset.action;
            if (!action) return;
            
            const handler = (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Visual feedback
                this._addClickFeedback(button);
                
                // Handle action via centralized system or scene-specific handler
                if (['dashboard', 'analytics', 'ppt-hero-analytics', 'hall-of-fame', 'game-center', 'profile', 'preferences', 'admin-panel', 'notifications', 'logout'].includes(action)) {
                    // All navigation and auth actions go through NavigationActionHandler
                    navigationHandler.handleTemplateAction(action, scene);
                } else if (scene.handleAction) {
                    // Scene-specific content actions (filters, toggles, etc.)
                    scene.handleAction(action, button);
                } else {
                    console.warn(`🤔 Unhandled header action: ${action}`);
                }
            };
            
            button.addEventListener('click', handler, { passive: false });
            this._trackEventHandler(scene.scene.key, button, 'click', handler);
        });
    }
    
    /**
     * Bind special events (filters, toggles, etc.)
     * @private
     */
    _bindSpecialEvents(scene, overlayId) {
        const overlay = document.getElementById(overlayId);
        if (!overlay) return;
        
        // Time filter buttons
        const timeButtons = overlay.querySelectorAll('.time-button[data-time]');
        timeButtons.forEach(button => {
            const handler = (e) => {
                e.preventDefault();
                this._handleTimeFilter(button, scene);
            };
            button.addEventListener('click', handler);
            this._trackEventHandler(scene.scene.key, button, 'click', handler);
        });
        
        // Filter tabs
        const filterTabs = overlay.querySelectorAll('.filter-tab[data-filter]');
        filterTabs.forEach(tab => {
            const handler = (e) => {
                e.preventDefault();
                this._handleFilterTab(tab, scene);
            };
            tab.addEventListener('click', handler);
            this._trackEventHandler(scene.scene.key, tab, 'click', handler);
        });
        
        // Export buttons
        const exportButtons = overlay.querySelectorAll('.export-btn[data-action="export"]');
        exportButtons.forEach(button => {
            const handler = (e) => {
                e.preventDefault();
                if (scene.handleExport) {
                    scene.handleExport();
                }
            };
            button.addEventListener('click', handler);
            this._trackEventHandler(scene.scene.key, button, 'click', handler);
        });
    }
    
    /**
     * Add optimized visual click feedback
     * Uses requestAnimationFrame for smooth performance
     * @private
     */
    _addClickFeedback(element) {
        // Use CSS classes for better performance
        element.classList.add('btn-professional');
        
        // Brief visual feedback
        requestAnimationFrame(() => {
            element.style.transform = 'scale(0.98)';
            element.style.opacity = '0.8';
            
            setTimeout(() => {
                element.style.transform = '';
                element.style.opacity = '';
            }, 150);
        });
    }
    
    /**
     * Handle time filter changes
     * @private
     */
    _handleTimeFilter(button, scene) {
        // Remove active from siblings
        const parent = button.parentElement;
        parent.querySelectorAll('.time-button').forEach(btn => btn.classList.remove('active'));
        
        // Add active to clicked button
        button.classList.add('active');
        
        // Notify scene if it has filter handler
        if (scene.handleTimeFilter) {
            scene.handleTimeFilter(button.dataset.time);
        }
    }
    
    /**
     * Handle filter tab changes
     * @private
     */
    _handleFilterTab(tab, scene) {
        // Remove active from siblings
        const parent = tab.parentElement;
        parent.querySelectorAll('.filter-tab').forEach(t => t.classList.remove('active'));
        
        // Add active to clicked tab
        tab.classList.add('active');
        
        // Notify scene if it has filter handler
        if (scene.handleFilter) {
            scene.handleFilter(tab.dataset.filter);
        }
    }
    
    /**
     * Track event handler for cleanup
     * @private
     */
    _trackEventHandler(sceneKey, element, event, handler) {
        if (!this.eventHandlers.has(sceneKey)) {
            this.eventHandlers.set(sceneKey, []);
        }
        this.eventHandlers.get(sceneKey).push({ element, event, handler });
    }
    
    /**
     * Check if scene has events bound
     * @param {string} sceneKey - Scene key to check
     * @returns {boolean} - Whether events are bound
     */
    hasEventsForScene(sceneKey) {
        return this.boundScenes.has(sceneKey);
    }
    
    /**
     * Reset all event bindings (for debugging)
     */
    resetAllBindings() {
        console.log('🔄 UnifiedEventBindingSystem: Resetting all bindings');
        this.boundScenes.clear();
        this.eventHandlers.clear();
    }
}

// Export singleton instance for easy access
export const eventBindingSystem = UnifiedEventBindingSystem.getInstance();
export default eventBindingSystem;
